/**
 * Counts non-fictional participants in a bracket column
 * @param {Object} col - Bracket column object
 * @returns {number} - Number of participants
 */
const getParticipantCount = col => {
  return (
    col?.rows?.filter(row => !row.isFiction && row.tournamentMemberId).length ||
    0
  )
}

/**
 * Determines round order based on round name pattern
 * @param {string} roundName - Name of the round
 * @returns {number} - Order value (lower = later round)
 */
const getRoundOrder = roundName => {
  if (!roundName) return 999

  const lowerName = roundName.toLowerCase()

  // Extract fraction from "1/4 final", "1/8 final", etc.
  const fractionMatch = lowerName.match(/(\d+)\/(\d+)/)
  if (fractionMatch) {
    return parseInt(fractionMatch[2]) // Denominator = round size
  }

  // Handle common patterns
  if (lowerName.includes('final') && !lowerName.includes('/')) return 1
  if (lowerName.includes('semi')) return 2
  if (lowerName.includes('quarter')) return 4
  if (lowerName.includes('1/8') || lowerName.includes('eighth')) return 8
  if (lowerName.includes('1/16') || lowerName.includes('sixteenth')) return 16
  if (lowerName.includes('1/32') || lowerName.includes('thirty-second'))
    return 32
  if (lowerName.includes('1/64') || lowerName.includes('sixty-fourth'))
    return 64

  // Extract any number as fallback
  const numberMatch = lowerName.match(/(\d+)/)
  return numberMatch ? parseInt(numberMatch[1]) : 999
}

/**
 * Sorts bracket columns in the correct tournament progression order
 * @param {Array} cols - Array of bracket column objects
 * @returns {Array} - Sorted array of bracket columns
 */
export const sortBracketColumns = cols => {
  if (!cols || !Array.isArray(cols)) {
    return cols
  }

  return [...cols].sort((a, b) => {
    // Primary sort: by participant count (more participants = earlier round)
    const aParticipants = getParticipantCount(a)
    const bParticipants = getParticipantCount(b)

    if (aParticipants !== bParticipants) {
      return bParticipants - aParticipants // Descending order
    }

    // Secondary sort: by round name pattern
    return getRoundOrder(b?.roundName) - getRoundOrder(a?.roundName)
  })
}

/**
 * Transforms and sorts bracket data to ensure correct column order
 * @param {Object} requestData - Raw bracket data from API
 * @returns {Object} - Transformed and sorted bracket data
 */
export const transformBracketData = requestData => {
  if (!requestData?.basket?.[0]?.cols) {
    return requestData
  }

  return {
    ...requestData,
    basket: [
      {
        ...requestData.basket[0],
        cols: sortBracketColumns(requestData.basket[0].cols),
      },
    ],
  }
}

/**
 * Groups array items into pairs (2 items each)
 * @param {Array} arr - Array to group
 * @returns {Array} - Array of pairs
 */
export const getArrayPairs = arr => {
  return (arr || []).reduce(function (result, value, index, array) {
    if (index % 2 === 0) result.push(array.slice(index, index + 2))
    return result
  }, [])
}

/**
 * Groups array items into fours (4 items each)
 * @param {Array} arr - Array to group
 * @returns {Array} - Array of fours
 */
export const getArrayFours = arr => {
  return (arr || []).reduce(function (result, value, index, array) {
    if (index % 4 === 0) result.push(array.slice(index, index + 4))
    return result
  }, [])
}

/**
 * Gets score value for display
 * @param {Object} val - Match participant object
 * @returns {string|number} - Score or '-'
 */
export const getScore = val => {
  if (!val.tournamentMemberId) {
    return '-'
  }

  if (Number.isFinite(val?.score)) {
    return val?.score
  }

  return '-'
}

/**
 * Gets CSS class for score based on winner
 * @param {Array} el - Array of match participants
 * @param {Object} block - Current participant block
 * @param {string} orangeClass - CSS class for winner
 * @returns {string} - CSS class name
 */
export const getClassScoreNew = (el, block, orangeClass) => {
  let maxScore = Math.max(el[0]?.score, el[1]?.score)
  if (!el) {
    return 'playerPontBg'
  }
  if (el[0]?.score === el[1]?.score) {
    return 'playerPontBg'
  }

  if (block?.score === maxScore) {
    return orangeClass
  }

  return 'playerPontBg'
}

/**
 * Creates placeholder data for desktop view
 * @param {Object} roundData - Round data object
 * @returns {Array} - Rows or placeholder
 */
export const createPlaceholderDataDesktop = roundData => {
  if (!roundData || !roundData.rows || roundData.rows.length === 0) {
    // Create 2 placeholder matches (4 players total) for empty rounds
    return [
      { id: '-', tournamentMemberId: null, score: null, matchId: null },
      { id: '-', tournamentMemberId: null, score: null, matchId: null },
      { id: '-', tournamentMemberId: null, score: null, matchId: null },
      { id: '-', tournamentMemberId: null, score: null, matchId: null },
    ]
  }

  return roundData.rows
}

/**
 * Creates placeholder data for mobile view
 * @param {Object} roundData - Round data object
 * @returns {Array} - Rows
 */
export const createPlaceholderDataMobile = roundData => {
  return roundData.rows
}

/**
 * Finds round index containing a specific match
 * @param {string} matchId - Match ID to find
 * @param {Object} data - Bracket data
 * @returns {number} - Round index
 */
export const findRoundIndexForMatch = (matchId, data) => {
  if (!matchId || !data?.basket?.length) {
    return 0
  }

  // Check Upper rounds first
  if (data.basket[0]?.cols) {
    const upperRoundIndex = data.basket[0].cols.findIndex(colsItem =>
      colsItem?.rows?.some(row => row?.matchId === matchId)
    )
    if (upperRoundIndex >= 0) {
      return upperRoundIndex
    }
  }

  // Check Lower rounds
  if (data.basket[1]?.cols) {
    const lowerRoundIndex = data.basket[1].cols.findIndex(colsItem =>
      colsItem?.rows?.some(row => row?.matchId === matchId)
    )
    if (lowerRoundIndex >= 0) {
      // Add the number of upper rounds to get the correct index in availableRounds
      const upperRoundsCount =
        data.basket[0]?.cols?.filter(
          colsItem => colsItem?.rows?.length && colsItem?.roundName
        ).length || 0

      return upperRoundsCount + lowerRoundIndex
    }
  }

  return 0
}
